import { useState, useEffect, useRef, Fragment } from "react";
import Link from "next/link";
import { Button } from "..";
import { Hamburger, Promote } from "@/components/bnp-icons";
import { useAuth } from "@/contexts/AuthContext";
import { CurrencySelector } from "@/components/bnp-ui/currency-selector";
import useNavItems, { TMenuItem } from "@/hooks/useNavItems";
import { usePathname } from "next/navigation";
import DashboardNav from "./DashboardNav";
import ProfessionalsDropdown from "./ProfessionalsDropdown";

export type { TMenuItem } from "@/hooks/useNavItems";
export { DashboardNav };

const PROFESSIONAL_TITLES = ["Realtors", "Developers"];

const Nav = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const { is_logged_in, user, status } = useAuth();
  const isLoading = status === "idle" || status === "loading";
  const pathname = usePathname();
  const menuRef = useRef<HTMLDivElement>(null);
  const { navItems: menuItems } = useNavItems();
  const professionalsItems = menuItems.filter((item) =>
    PROFESSIONAL_TITLES.includes(item.title),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        setOpenDropdown(null);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  const toggleDropdown = (title: string) => {
    setOpenDropdown(openDropdown === title ? null : title);
  };

  const isDropdownOpen = (title: string) => openDropdown === title;

  const regularItems = menuItems.filter(
    (item) => !PROFESSIONAL_TITLES.includes(item.title)
  );

  const isAdmin =
    (user as any)?.role === "regular-admin" ||
    (user as any)?.role === "super-admin";

  const isActiveRoute = (activePaths: string[] = []) => {
    if (!pathname) return false;
    if (pathname === "/" && activePaths.includes("/")) return true;
    return activePaths.some(
      (path) =>
        path !== "/" && (pathname === path || pathname.startsWith(`${path}/`)),
    );
  };

  return (
    <nav className="sticky top-0 z-50 w-[100%] lg:px-4 mobile-nav lg:[box-shadow:none]">
      <div className="font-sans w-[100%]  lg:max-w-[1540px] mx-auto flex justify-between items-center bg-bg-white border-b lg:border border-soft-200 px-4 lg:px-6 xl:px-10 py-3 lg:py-6 rounded-lg lg:rounded-[90px] h-[64px] lg:h-[94px] relative lg:shadow-sm">
<Link
  href="/"
  className="justify-start items-center flex gap-2 hover:opacity-90 transition-opacity"
  aria-label="FindNigeriaProperties.com Home"
  style={{ minHeight: "44px" }}
>
  <img
    src="/fnp-logo-with-credibility-subtext-dark-teal.png"
    alt="FindNigeriaProperties.com"
    className="w-auto h-auto max-w-[220px] relative top-1 object-contain"
  />
</Link>
        <div className="hidden lg:flex items-center justify-center gap-4 xl:gap-6">
          {regularItems.map((item: TMenuItem) => {
            return (
              <Fragment key={item.path || item.title}>
                <Link
                  href={item.path || "#"}
                className={`relative py-2 text-sm font-semibold transition-colors hover:text-bnp-cyan whitespace-nowrap
                ${
                  isActiveRoute(item.activePaths)
                    ? "text-bnp-cyan after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-bnp-cyan"
                    : "text-blue-primary"
                }`}
              >
                {item.title === "Deals" ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-flex items-center justify-center w-5 h-5 bg-red-base rounded-full">
                      <Promote />
                    </span>
                    {item.title}
                  </span>
                ) : (
                  item.title
                )}
              </Link>
              {item.title === "Property Request" &&
                professionalsItems.length > 0 && (
                  <ProfessionalsDropdown
                    items={professionalsItems.map((item) => ({
                      title: item.title,
                      path: item.path || "",
                      activePaths: item.activePaths || [],
                    }))}
                    pathname={pathname}
                  />
                )}
            </Fragment>
          );
        })}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <CurrencySelector />
          {!isLoading &&
            (is_logged_in && user ? (
              <>
                <Link
                  href="/user/dashboard"
                  className="flex items-center gap-3 hover:opacity-90 transition-opacity"
                >
                  <div className="w-10 h-10 rounded-full bg-[#2D96A7]/10 flex items-center justify-center overflow-hidden border border-[#2D96A7]/30 flex-shrink-0">
                    {user.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt={user.full_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-bold text-[#2D96A7]">
                        {user.full_name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)}
                      </span>
                    )}
                  </div>
                  {/* Ensure the parent of this container also has `min-w-0` or `overflow-hidden` if nested inside another flex container */}

                  <div className="flex flex-col min-w-0 w-full max-w-[140px] overflow-hidden">
                    <span className="text-sm font-semibold text-gray-900 truncate w-full block">
                      {user.full_name}
                    </span>
                    <span className="text-xs text-gray-500 truncate w-full block">
                      {user.email}
                    </span>
                  </div>
                </Link>
                {isAdmin && (
                  <Link href="/admin/user/dashboard">
                    <Button className="bg-bnp-cyan text-white hover:bg-bnp-cyan/90 transition-colors text-sm font-semibold px-6 h-10 rounded-lg">
                      Admin Dashboard
                    </Button>
                  </Link>
                )}
              </>
            ) : (
              <>
                <Link href="/auth/sign-in">
                  <Button className="bg-white border border-border-1 hover:bg-gray-50 transition-colors text-bnp-dark text-sm font-semibold px-6 h-10 rounded-lg">
                    Login
                  </Button>
                </Link>
                <Link href="/auth/sign-up">
                  <Button className="bg-bnp-dark text-white hover:bg-bnp-dark/90 transition-colors text-sm font-semibold px-6 h-10 rounded-lg">
                    Sign up
                  </Button>
                </Link>
              </>
            ))}
        </div>

        <div className="lg:hidden">
          <Hamburger
            isOpen={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          />
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-50 mobile-menu-overlay
          ${isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!isMenuOpen}
        onClick={() => setIsMenuOpen(false)}
      >
        <div
          ref={menuRef}
          className={`fixed right-0 top-0 h-full w-[280px] bg-white shadow-xl transform transition-transform duration-300 ease-out mobile-menu-panel
            ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col h-full">
            <div className="flex justify-end p-4">
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close menu"
                style={{ minWidth: "44px", minHeight: "44px" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="px-4 pt-2 pb-1">
                <CurrencySelector />
              </div>
              <nav className="flex flex-col px-4">
                {menuItems
                  .filter((item) => !item.hidden)
                  .map((item: TMenuItem) => (
                    <Link
                      href={item.path || "#"}
                      key={item.path || item.title}
                      onClick={() => setIsMenuOpen(false)}
                      className={`py-4 px-4 text-sm font-semibold rounded-lg transition-colors mobile-nav-item
                      ${
                        isActiveRoute(item.activePaths || [])
                          ? "text-bnp-cyan bg-soft-200"
                          : "text-blue-primary hover:bg-gray-50"
                      }`}
                      style={{ minHeight: "44px" }}
                    >
                      {item.title === "Deals" ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-flex items-center justify-center w-5 h-5 bg-red-base rounded-full">
                            <Promote />
                          </span>
                          {item.title}
                        </span>
                      ) : (
                        item.title
                      )}
                    </Link>
                  ))}
              </nav>
            </div>

            <div className="p-4 border-t border-gray-200">
              {!isLoading &&
                (is_logged_in && user ? (
                  <div className="flex flex-col gap-3">
                    <Link
                      href="/user/dashboard"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#2D96A7]/10 flex items-center justify-center overflow-hidden border border-[#2D96A7]/30 flex-shrink-0">
                        {user.profile_picture ? (
                          <img
                            src={user.profile_picture}
                            alt={user.full_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-bold text-[#2D96A7]">
                            {user.full_name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2)}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {user.full_name}
                        </span>
                        <span className="text-xs text-gray-500 truncate">
                          {user.email}
                        </span>
                      </div>
                    </Link>
                    {isAdmin && (
                      <Link
                        href="/admin/user/dashboard"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Button className="w-full bg-bnp-cyan text-white hover:bg-bnp-cyan/90 transition-colors text-sm font-semibold h-12 rounded-lg">
                          Admin Dashboard
                        </Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <Link
                      href="/auth/sign-in"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Button className="w-full bg-white border border-border-1 hover:bg-gray-50 transition-colors text-bnp-dark text-sm font-semibold h-12 rounded-lg">
                        Login
                      </Button>
                    </Link>
                    <Link
                      href="/auth/sign-up"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Button className="w-full bg-bnp-dark text-white hover:bg-bnp-dark/90 transition-colors text-sm font-semibold h-12 rounded-lg">
                        Sign up
                      </Button>
                    </Link>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Nav;
